/**
 * Shared navigation model — single source of truth for ALL navigation
 * destinations across BottomNav (mobile) and CustomerDesktopSidebar (lg+).
 *
 * Each consumer filters this superset for its own layout:
 *   - BottomNav V1A (browse on / FR-01 HARD GATE): primary exactly
 *     browse|scan|orders — never G4 trailing-promote `home`, never shops append,
 *     regardless of favorites/shops flags (product-recommended both true → still 3).
 *   - BottomNav V1B (browse off + favorites + shops + scan ON): primary
 *     home|scan|orders then shops append → Oblíbené|Skenovat|Objednávky|Obchody
 *     (`'home'` ↔ BottomNavTab `'shop'` via destinationIdToTab).
 *   - CustomerDesktopSidebar: browse (when showBrowse), home/favorites, scan,
 *     orders, shops + signed-in extras — `'home'` at most once (NEW G3 / D36);
 *     browse+home as distinct Nakupovat+Oblíbené is correct.
 *
 * Auth/tenant/flag logic lives here — not in individual components.
 */

import { useMemo } from 'react';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { useAccountMe } from '../account/accountApi.js';
import { shouldHideBottomNav } from '../../app/bottomNavActiveTab.js';
import {
  PLATFORM_BROWSE_PATH,
  resolveShopHref,
} from '../shop/resolveShopHref.js';
import { useCustomerNavModel } from './useCustomerNavModel.js';
import type { GiftSurfaceVisibility } from './giftSurfaceVisibility.js';
import {
  CouponIcon,
  HeartHandIcon,
  HistoryIcon,
  HomeIcon,
  ReceiptIcon,
  RefreshIcon,
  ScanIcon,
  ShopsDirectoryIcon,
} from '../../shared/ui/BottomNavIcons.js';

// Re-export for consumers that need pathname-aware hide behavior.
export { shouldHideBottomNav } from '../../app/bottomNavActiveTab.js';

// Sidebar-friendly helper: hide only from verification requirement.
export function shouldHideNavigation(mustCompleteVerification: boolean): boolean {
  return shouldHideBottomNav('/', '', { mustCompleteVerification });
}

/** Desktop sidebar test ids — distinct from mobile bottom nav (both can exist in DOM at lg). */
export function resolveDesktopSidebarTestId(bottomNavTestId: string): string {
  if (bottomNavTestId.startsWith('customer-desktop-sidebar-')) {
    return bottomNavTestId;
  }
  if (bottomNavTestId.startsWith('customer-bottom-nav-')) {
    return bottomNavTestId.replace('customer-bottom-nav-', 'customer-desktop-sidebar-');
  }
  return bottomNavTestId;
}

// ─── Icon type ────────────────────────────────────────────────────────────────

/** Icon component signature used by all navigation entries. */
export type NavIconComponent = typeof HomeIcon;

// ─── Destination IDs ──────────────────────────────────────────────────────────

/**
 * Every possible navigation destination id across all surfaces.
 * BottomNav and sidebar each pick a subset.
 */
export type NavigationDestinationId =
  // Primary — V1A browse|scan|orders; V1B home|scan|orders (+ shops append)
  | 'browse'     // = Nakupovat — discovery (/browse); V1A primary when showBrowse
  | 'home'       // = Oblíbené — V1B bottom-nav primary when browse off + showFavorites; sidebar when flagged
  | 'scan'       // in-store scanner
  | 'orders'     // /account/orders
  | 'shops'      // public directory /shops (sidebar always when flagged; mobile append only when browse off)
  // Sidebar-only primary
  | 'loyalty'    // /account/orders?tab=loyalty
  // Overflow (BottomNav Více / potential sidebar extras)
  | 'donate'
  | 'recurring'
  | 'my-donations'
  | 'find-order'
  | 'overview';  // /account (kupony / account home hub)

// ─── Visibility rule ─────────────────────────────────────────────────────────

/**
 * Coarse visibility guard for the destination:
 *   always    — shown to all visitors (including guests)
 *   signedIn  — shown only when isGloballySignedInChrome is true
 *   guest     — shown only to unsigned-in visitors (reserved for future use)
 */
export type NavigationVisibility = 'always' | 'signedIn' | 'guest';

// ─── Destination type ────────────────────────────────────────────────────────

export interface NavigationDestination {
  readonly id: NavigationDestinationId;
  readonly labelKey: string;
  readonly testId: string;
  readonly Icon: NavIconComponent;
  /** Resolved absolute href ready for <NavLink to={href}>. */
  readonly href: string;
  /**
   * Coarse visibility guard. Consumers may apply additional flag/entitlement
   * checks on top — this captures the auth requirement only.
   */
  readonly visibility: NavigationVisibility;
}

// ─── Build input ─────────────────────────────────────────────────────────────

export interface BuildNavigationDestinationsInput {
  readonly tenantCode: string;
  /**
   * Pre-resolved href for Oblíbené (starred-shops hub) — typically
   * `/favorites` (guest and signed-in), never lastKnown SP catalog.
   */
  readonly shopHref: string;
  readonly isAuthenticated: boolean;
  /** Gift surface visibility — omit (or pass undefined) when not yet resolved. */
  readonly gift?: GiftSurfaceVisibility;
  /** Show Najít objednávku (order pickup infra). */
  readonly showFindOrder?: boolean;
  /** Show Kupóny / overview hub (/account). */
  readonly showKupony?: boolean;
  /** Show Oblíbené (starred-shops hub) in bottom-nav + desktop sidebar. */
  readonly showFavorites?: boolean;
  /** Show Obchody (`/shops`) in desktop sidebar; mobile bottom append only when browse off (V1B). */
  readonly showShops?: boolean;
  /** Show desktop sidebar Věrnost entry (/account/orders?tab=loyalty). */
  readonly showLoyalty?: boolean;
  /** Show Nakupovat (`/browse`) in bottom-nav + desktop sidebar. Callers must pass explicit boolean from `isCustomerBrowseEnabled` (typically via `useCustomerNavModel.showBrowse`). */
  readonly showBrowse?: boolean;
  /** Show Skenovat (`/scan`) in bottom-nav + desktop sidebar. Callers must pass explicit boolean from `isCustomerScanEnabled`. */
  readonly showScan?: boolean;
}

// ─── Href builders ───────────────────────────────────────────────────────────

/**
 * Platform Scan hub — always `/scan`.
 * Tenant-scoped `/{tenant}/shop/scanner` is wrong under Favorites (`__platform__`).
 */
export function resolveScanHref(_tenantCode?: string): string {
  return '/scan';
}

function resolveHomeHref(tenantCode: string, shopHref: string): string {
  // Prefer the caller-supplied shopHref (accounts for platform chrome tenant),
  // but fall back to the tenant-relative /home path used by the sidebar.
  return shopHref !== '' ? shopHref : `/${encodeURIComponent(tenantCode)}/home`;
}

// ─── Main builder ────────────────────────────────────────────────────────────

/**
 * Returns ALL navigation destinations available given the current auth, tenant,
 * and feature-flag context.
 *
 * Callers filter the returned list by `id` to pick their layout subset:
 *   BottomNav primary (browse on / FR-01): 'browse' | 'scan' | 'orders' — never 'home'
 *   BottomNav primary (browse off + favorites): 'home' | 'scan' | 'orders'
 *   BottomNav shops tab: 'shops' when showShops AND browse off (appended after trailing)
 *   BottomNav overflow: 'overview' | 'donate' | 'recurring' | 'my-donations' | 'find-order'
 *   Sidebar: 'browse' (when showBrowse) | 'home' | 'scan' | 'orders' | 'shops' | 'loyalty'
 *
 * Destinations whose flag conditions are not met are excluded from the result
 * (not included with visibility=false) so callers can iterate without filtering
 * for flag conditions they don't know about.
 */
export function buildNavigationDestinations(
  input: BuildNavigationDestinationsInput,
): NavigationDestination[] {
  const {
    tenantCode,
    shopHref,
    isAuthenticated,
    gift,
    showFindOrder,
    showKupony,
    showFavorites,
    showShops,
    showLoyalty,
    showBrowse,
    showScan,
  } = input;

  const destinations: NavigationDestination[] = [];

  // ── Always-visible primary (CUSTOMER FIX Stage 2 IA) ────────────────────
  if (showBrowse === true) {
    destinations.push({
      id: 'browse',
      labelKey: 'nav.browse',
      testId: 'customer-bottom-nav-browse',
      Icon: HomeIcon,
      href: PLATFORM_BROWSE_PATH,
      visibility: 'always',
    });
  }

  if (showFavorites === true) {
    destinations.push({
      id: 'home',
      labelKey: 'nav.shop',
      testId: 'customer-bottom-nav-shop',
      Icon: HeartHandIcon,
      href: resolveHomeHref(tenantCode, shopHref),
      visibility: 'always',
    });
  }

  if (showScan === true) {
    destinations.push({
      id: 'scan',
      labelKey: 'nav.scan',
      testId: 'customer-bottom-nav-scan',
      Icon: ScanIcon,
      href: resolveScanHref(tenantCode),
      visibility: 'always',
    });
  }

  if (showShops === true) {
    destinations.push({
      id: 'shops',
      labelKey: 'nav.shops',
      testId: 'customer-bottom-nav-shops',
      Icon: ShopsDirectoryIcon,
      href: '/shops',
      visibility: 'always',
    });
  }

  // Purchases / Objednávky — always in chrome (guest click auth-gates like BottomNav).
  destinations.push({
    id: 'orders',
    labelKey: 'nav.orders',
    testId: 'customer-bottom-nav-orders',
    Icon: HistoryIcon,
    href: '/account/orders',
    visibility: 'always',
  });

  // ── Auth-gated extras (sidebar) ─────────────────────────────────────────
  if (isAuthenticated && showLoyalty === true) {
    destinations.push({
      id: 'loyalty',
      labelKey: 'nav.loyalty',
      testId: 'customer-desktop-sidebar-loyalty',
      Icon: HistoryIcon,
      href: '/account/orders?tab=loyalty',
      visibility: 'signedIn',
    });
  }

  // ── Overflow / Více — same membership as buildCustomerNavComposition (guests included)
  // overview (kupony hub) — flag: showKupony
  if (showKupony === true) {
    destinations.push({
      id: 'overview',
      labelKey: 'account.menu.home.label',
      testId: 'customer-bottom-nav-overview',
      Icon: CouponIcon,
      href: '/account',
      visibility: 'always',
    });
  }

  // Gift overflow — each gated by its individual flag in GiftSurfaceVisibility
  if (gift?.donateVisible === true) {
    destinations.push({
      id: 'donate',
      labelKey: 'hub.donate.label',
      testId: 'customer-bottom-nav-donate',
      Icon: HeartHandIcon,
      href: '/account/donate',
      visibility: 'always',
    });
  }

  if (gift?.recurringVisible === true) {
    destinations.push({
      id: 'recurring',
      labelKey: 'account.menu.recurring.label',
      testId: 'customer-bottom-nav-recurring',
      Icon: RefreshIcon,
      href: '/account/recurring-donations',
      visibility: 'always',
    });
  }

  if (gift?.myDonationsVisible === true) {
    destinations.push({
      id: 'my-donations',
      labelKey: 'account.menu.donations.label',
      testId: 'customer-bottom-nav-my-donations',
      Icon: ReceiptIcon,
      href: '/account/donations',
      visibility: 'always',
    });
  }

  // find-order (order pickup infra)
  if (showFindOrder === true) {
    destinations.push({
      id: 'find-order',
      labelKey: 'hub.orders.label',
      testId: 'customer-more-find-order',
      Icon: HistoryIcon,
      href: '/orders/find',
      visibility: 'always',
    });
  }

  return destinations;
}

// ─── Convenience filter helpers ───────────────────────────────────────────────

/** IDs used by BottomNav primary slots when browse on (V1A / FR-01) — left → right. */
export const BOTTOM_NAV_PRIMARY_IDS: readonly NavigationDestinationId[] = [
  'browse',
  'scan',
  'orders',
] as const;

/** Primary bottom-nav ids when Nakupovat is off and Oblíbené is not primary (favorites off). */
export const BOTTOM_NAV_PRIMARY_IDS_WITHOUT_BROWSE: readonly NavigationDestinationId[] = [
  'scan',
  'orders',
] as const;

/** V1B product primary ids (browse off + favorites on + scan ON) — before shops append. */
export const BOTTOM_NAV_PRIMARY_IDS_V1B: readonly NavigationDestinationId[] = [
  'home',
  'scan',
  'orders',
] as const;

/**
 * Resolve ordered bottom-nav primary destination ids.
 * FR-01: browse on → never prepend `'home'` regardless of `showFavorites`.
 * V1B: browse off + favorites on → `'home'` leftmost, then scan + orders.
 */
export function resolveBottomNavPrimaryIds(
  showBrowse?: boolean,
  showScan?: boolean,
  showFavorites?: boolean,
): readonly NavigationDestinationId[] {
  const ids: NavigationDestinationId[] = [];
  if (showBrowse === true) {
    ids.push('browse');
  } else if (showFavorites === true) {
    ids.push('home');
  }
  if (showScan === true) {
    ids.push('scan');
  }
  ids.push('orders');
  return ids;
}

/** IDs used by BottomNav overflow (Více / single-promoted slot). Obchody is not here — preserved via sidebar + /shops + CTAs so Více stays conditional (T9). */
export const BOTTOM_NAV_OVERFLOW_IDS: readonly NavigationDestinationId[] = [
  'overview',
  'donate',
  'recurring',
  'my-donations',
  'find-order',
] as const;

/**
 * IDs used by CustomerDesktopSidebar (fixed order).
 * NEW G3 / D36: `'home'` appears at most once; `'browse'`+`'home'` as distinct
 * Nakupovat+Oblíbené is allowed — never duplicate Oblíbené/`'home'`.
 */
export const DESKTOP_SIDEBAR_IDS: readonly NavigationDestinationId[] = [
  'browse',
  'home',
  'scan',
  'orders',
  'shops',
  'loyalty',
] as const;

/**
 * Filter a destination list to a specific ordered id subset.
 * Destinations absent from `allDestinations` (e.g. auth-gated items for guests)
 * are silently omitted — the caller gets only what is currently available.
 * NEW G3 / D36: each id is emitted at most once (no duplicate `'home'`).
 */
export function filterDestinationsByIds(
  allDestinations: readonly NavigationDestination[],
  ids: readonly NavigationDestinationId[],
): NavigationDestination[] {
  const byId = new Map(allDestinations.map((d) => [d.id, d]));
  const result: NavigationDestination[] = [];
  const seen = new Set<NavigationDestinationId>();
  for (const id of ids) {
    if (seen.has(id)) {
      continue;
    }
    const dest = byId.get(id);
    if (dest !== undefined) {
      seen.add(id);
      result.push(dest);
    }
  }
  return result;
}

/**
 * Bottom-nav trailing slot candidates: standard overflow only.
 * FR-01 HARD GATE (D2/D27/D30): when `showBrowse === true`, never G4-promote
 * `'home'` — favorites stay reachable via More / `/favorites` / Home ☆.
 * V1B: `'home'` is already a primary — never duplicate as trailing.
 */
export function buildBottomNavTrailingDestinationCandidates(
  allDestinations: readonly NavigationDestination[],
  options?: { readonly showBrowse?: boolean },
): NavigationDestination[] {
  const overflow = filterDestinationsByIds(allDestinations, BOTTOM_NAV_OVERFLOW_IDS);
  if (overflow.length > 0) {
    return overflow;
  }
  // Browse on: suppress G4 home promote. Browse off: home is primary when favorites on.
  void options?.showBrowse;
  return [];
}

// ─── React hook ───────────────────────────────────────────────────────────────

/**
 * Hook that assembles all navigation destinations for the current user/tenant
 * context. Bridges `customerNavigationModel` with the existing flag/entitlement
 * resolution in `useCustomerNavModel` so consumers (e.g. CustomerDesktopSidebar)
 * can call a single hook without duplicating auth/flag logic.
 *
 * BottomNav continues to use `useCustomerNavModel` directly — this hook is
 * intended for new consumers (sidebar, future surfaces) that work with the
 * `NavigationDestination[]` shape rather than `NavComposition`.
 */
export interface UseNavigationDestinationsResult {
  readonly destinations: NavigationDestination[];
  readonly hideNavigation: boolean;
  readonly showBrowse: boolean;
  readonly showScan: boolean;
  readonly showFavorites: boolean;
}

export function useNavigationDestinations(): UseNavigationDestinationsResult {
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const me = useAccountMe(session !== null);
  const { giftForOverflow, showFindOrder, showKupony, showFavorites, showShops, showLoyalty, showBrowse, showScan } =
    useCustomerNavModel();

  const isAuthenticated = isGloballySignedInChrome({ session });

  const shopHref = useMemo(() => {
    // Oblíbené hub — starred shops list, not lastKnown SP catalog shortcut.
    return resolveShopHref(undefined, session);
  }, [session]);

  const destinations = useMemo(
    () =>
      buildNavigationDestinations({
        tenantCode,
        shopHref,
        isAuthenticated,
        gift: giftForOverflow,
        showFindOrder,
        showKupony,
        showFavorites,
        showShops,
        showLoyalty,
        showBrowse,
        showScan,
      }),
    [
      tenantCode,
      shopHref,
      isAuthenticated,
      giftForOverflow,
      showFindOrder,
      showKupony,
      showFavorites,
      showShops,
      showLoyalty,
      showBrowse,
      showScan,
    ],
  );

  const hideNavigation = shouldHideNavigation(me.data?.mustCompleteVerification ?? false);

  return { destinations, hideNavigation, showBrowse, showScan, showFavorites };
}
